import { AxiosError, AxiosResponse } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import { DisqualificationFormPayload } from '@/forms/DisqualificationForm/types';
import { PersonEnrollmentFormPayload } from '@/forms/EnrollmentForm/types';
import { RejectionFormPayload } from '@/forms/RejectionForm/types';
import { DisqualificationReason } from '@/types/disqualificationReason';
import { IntakeRejectionReason } from '@/types/intakeRejectionReason';
import { ProgramQualification } from '@/types/person';
import { PhaseType } from '@/types/phaseType';
import { Program } from '@/types/program';
import { ProgramQualificationPayload } from '@/types/programQualification';
import { ProgramQualificationDisqualification } from '@/types/programQualificationDisqualification';
import { QueryParams } from '@/types/query';
import RepositoryResponse from '@/types/repositoryResponse';
import ajax from '@/utils/ajax';
import defaultRepositoryError from '@/utils/defaultRepositoryError';
import logAjaxError from '@/utils/logAjaxError';

export const programRepository = {
  async getPrograms({
    host,
    params = {}
  }: {
    host: string;
    params?: QueryParams;
  }): Promise<RepositoryResponse<Program[]>> {
    try {
      const path = `/api/dugout/program/?active=true`;
      const response = await ajax.get({
        url: `${host}${path}`,
        params
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get programs.');
    }
  },

  async getPhaseTypesForProgram({
    host,
    programId
  }: {
    host: string;
    programId: string;
  }): Promise<RepositoryResponse<PhaseType[]>> {
    try {
      const path = `/api/dugout/program/${programId}/phase_types/`;
      const response = await ajax.get({
        url: `${host}${path}`
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to get phase types for program.');
    }
  },

  async getIntakeRejectionReasons({ host }: { host: string }): Promise<IntakeRejectionReason[]> {
    const path = '/api/dugout/program_qualification/intake_rejection_reasons/';
    const response = await ajax.get({
      url: `${host}${path}`
    });

    return response.data;
  },

  async getDisqualificationReasons({ host }: { host: string }): Promise<DisqualificationReason[]> {
    const path = '/api/dugout/program_qualification/disqualification_reasons/';
    const response = await ajax.get({
      url: `${host}${path}`
    });

    return response.data;
  },

  async enrollPerson({
    host,
    params,
    programQualificationId
  }: {
    host: string;
    params: PersonEnrollmentFormPayload;
    programQualificationId: string;
  }): Promise<RepositoryResponse<void>> {
    try {
      const path = `/api/dugout/program_qualification/${programQualificationId}/enroll/`;
      const response = await ajax.post<PersonEnrollmentFormPayload, AxiosResponse>({
        url: `${host}${path}`,
        data: params
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to enroll.');
    }
  },

  async rejectPerson({
    host,
    params,
    programQualificationId
  }: {
    host: string;
    params: RejectionFormPayload | DisqualificationFormPayload;
    programQualificationId: string;
  }): Promise<RepositoryResponse<ProgramQualificationDisqualification>> {
    try {
      const path = `/api/dugout/program_qualification/${programQualificationId}/reject/`;
      const response = await ajax.post<
        RejectionFormPayload | DisqualificationFormPayload,
        AxiosResponse
      >({
        url: `${host}${path}`,
        data: params
      });

      return {
        data: response.data,
        isError: false
      };
    } catch (error) {
      logAjaxError(error);
      return defaultRepositoryError(error as AxiosError, 'Failed to reject person.');
    }
  },

  async createProgramQualification({
    host,
    params
  }: {
    host: string;
    params: ProgramQualificationPayload;
  }): Promise<ProgramQualification> {
    const path = `/api/dugout/program_qualification/`;
    const response = await ajax.post<ProgramQualificationPayload, AxiosResponse>({
      url: `${host}${path}`,
      data: params
    });

    return response.data;
  },

  async updateProgramQualification({
    host,
    params
  }: {
    host: string;
    params: ProgramQualificationPayload;
  }): Promise<ProgramQualification> {
    const path = `/api/dugout/program_qualification/${params.id}/`;
    const response = await ajax.put<ProgramQualificationPayload, AxiosResponse>({
      url: `${host}${path}`,
      data: params
    });

    return response.data;
  }
};

const ProgramRepository = () => {
  const { host } = useContext(AppContext);

  return {
    async getPrograms(params: QueryParams): Promise<RepositoryResponse<Program[]>> {
      return programRepository.getPrograms({ host, params });
    },

    async getAllPrograms(): Promise<RepositoryResponse<Program[]>> {
      return programRepository.getPrograms({ host, params: { all_phase_types: true } });
    },

    async getPhaseTypesForProgram({
      programId
    }: {
      programId: string;
    }): Promise<RepositoryResponse<PhaseType[]>> {
      return programRepository.getPhaseTypesForProgram({ host, programId });
    },

    async getIntakeRejectionReasons(): Promise<IntakeRejectionReason[]> {
      return programRepository.getIntakeRejectionReasons({ host });
    },

    async getDisqualificationReasons(): Promise<DisqualificationReason[]> {
      return programRepository.getDisqualificationReasons({ host });
    },

    async enrollPerson({
      params,
      programQualificationId
    }: {
      params: PersonEnrollmentFormPayload;
      programQualificationId: string;
    }): Promise<RepositoryResponse<void>> {
      return programRepository.enrollPerson({ host, params, programQualificationId });
    },

    async rejectPerson({
      params,
      programQualificationId
    }: {
      params: RejectionFormPayload | DisqualificationFormPayload;
      programQualificationId: string;
    }): Promise<RepositoryResponse<ProgramQualificationDisqualification>> {
      return programRepository.rejectPerson({ host, params, programQualificationId });
    },

    async createProgramQualification({
      params
    }: {
      params: ProgramQualificationPayload;
    }): Promise<ProgramQualification> {
      return programRepository.createProgramQualification({ host, params });
    },

    async updateProgramQualification({
      params
    }: {
      params: ProgramQualificationPayload;
    }): Promise<ProgramQualification> {
      return programRepository.updateProgramQualification({ host, params });
    }
  };
};

export default ProgramRepository;
